import {
  SearchFilterSelectorContext,
  SearchFilterSelectorContextType,
} from "@/features/search-filter/context/searchFilterSelectorContext";
import {
  NoShowLateHandlingStatus,
  NoShowLateHandlingType,
} from "@/features/search-filter/types/FilterEntitys";
import { Card } from "@stenajs-webui/elements";
import * as React from "react";
import { useContext, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import { NOSHOW_LATE_EDIT } from "../../../types/claims/constants";
import { hasClaim } from "../../../lib/claims/utils";
import { PageLayout } from "@/components/page-layout/PageLayout";
import { UnAuthorizedView } from "@/components/unauthorized-view/UnAuthorizedView";
import { FreightPricingPortalError } from "../../../types/error/FreightPricingPortalError";
import { profileSelectors } from "@/redux/profile/selectors";
import { NoShowLateHandlingHandleBookingsCustomerDataTable } from "./NoShowLateHandlingHandleBookingsCustomerDataTable";
import { NoShowLateHandlingHandleBookingsHeader } from "./NoShowLateHandlingHandleBookingsHeader";
import { NoShowLateHandlingHandleBookingsTableHeader } from "./NoShowLateHandlingHandleBookingsTableHeader";
import { useNoShowLateHandlingBookingListQuery } from "./hooks/UseNoShowLateHandlingBookingListQuery";
import { clearSelectionState } from "./redux/reducers";
import { noShowLateHandlingBookingListDataFilterVariablesTransformer } from "./transformers/NoShowLateHandlingBookingListDataFilterVariablesTransformer";
import {
  createFppSearchFilterActions,
  filterCommittedActions,
} from "@/features/search-filter/redux/reducer";

interface Props {}

export interface HandlingCategories {
  handlingStatuses?: Array<NoShowLateHandlingStatus>;
  handlingTypes?: Array<NoShowLateHandlingType>;
  error?: string;
}

export const NoShowLateHandlingHandleBookingsScreen: React.FC<Props> = () => {
  const { workspaceState } = useContext(
    SearchFilterSelectorContext
  ) as SearchFilterSelectorContextType;

  const { claims } = useSelector(profileSelectors.getGlobal);
  const dispatch = useDispatch();

  const [localErrors, setLocalErrors] = useState<FreightPricingPortalError[]>(
    []
  );
  const {
    error,
    loading,
    noShowLateHandlingBookingData,
    fetchNoShowLateHandlingBookings,
  } = useNoShowLateHandlingBookingListQuery();

  const onSearch = () => {
    fetchNoShowLateHandlingBookings({
      variables: {
        filterOptions:
          noShowLateHandlingBookingListDataFilterVariablesTransformer(
            workspaceState.formModel
          ),
      },
    });
  };

  const searchFilterActions = useMemo(
    () => createFppSearchFilterActions("noShowLateHandlingHandleBookings"),
    []
  );

  useEffect(() => {
    dispatch(clearSelectionState());
    return () => {
      dispatch(filterCommittedActions.clearAll());
      dispatch(searchFilterActions.clearFormModel());
    };
  }, [dispatch, searchFilterActions]);

  const numberOfCustomers =
    noShowLateHandlingBookingData?.map((data) => data.customer.custNo).length ??
    0;

  const numberOfBookings =
    noShowLateHandlingBookingData?.map((data) => data.customerBookings).flat()
      .length ?? 0;

  const bookingsLoading = loading && !noShowLateHandlingBookingData;

  return (
    <>
      <Helmet title="Handle bookings - NSLH - FPP" />
      <NoShowLateHandlingHandleBookingsHeader />
      {hasClaim(claims, [NOSHOW_LATE_EDIT]) ? (
        <PageLayout>
          <Card>
            <NoShowLateHandlingHandleBookingsTableHeader
              customers={numberOfCustomers}
              bookings={numberOfBookings}
              noShowLateHandlingBookingsData={
                noShowLateHandlingBookingData ?? []
              }
              onSearch={onSearch}
              localErrors={localErrors}
              setLocalErrors={setLocalErrors}
            />
            <NoShowLateHandlingHandleBookingsCustomerDataTable
              error={error}
              loading={bookingsLoading}
              noShowLateHandlingBookingsData={
                noShowLateHandlingBookingData ?? []
              }
            />
          </Card>
        </PageLayout>
      ) : (
        <UnAuthorizedView />
      )}
    </>
  );
};
